import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Grid, Box, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { openModal, closeModal } from "../../redux/reducers/dialog";
import { toast } from "react-toastify";
import {
  useDeleteFolder,
  useUpdateFolder,
  useRemoveWheelFromFolder,
} from "../../services/mutations";
import FolderTile from "./FolderTile";
import FoldersHeader from "./FolderHeader";
import CreateFolderModal from "./CreateFolderModal";
import ListHeader from "../WheelGallery/WheelListing/ListComponents/ListHeader";
import { useGetUserFolders } from "../../services/queries";
import { useCreateFolder } from "../../services/mutations";
import { getLoggedInUser, isUserLoggedIn } from "../../utils/helpers";
import { useParams } from "react-router-dom";
import FolderSkeleton from "./FolderSkeleton";
import _ from "lodash";
import WheelCard from "../WheelGallery/WheelListing/ListComponents/WheelCard";
import MoveToFolderModal from "./MoveToFoldersModal";
import EmptyState from "./EmptyFolderView";
import { is } from "date-fns/locale";
import ListView from "./FolderListView";
import WheelListView from "../WheelGallery/WheelListing/TabComponents/ListView";
import { colors } from "../../utils";
import { getRemoveFromFolderMenuItem } from "../../utils/wheelMenuHelpers";
import { menuItemKeys } from "../WheelCardThreeDotMenu/WheelCardThreeDotMenu";
import { useQueryClient } from "react-query";

const Folders = ({ tabChild }) => {
  const [searchedText, setSearchedText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [isGridSelect, setIsGridSelect] = useState(true);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [moveModalData, setMoveModalData] = useState({
    isOpen: false,
    sourceId: null,
    sourceType: null,
  });
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const deleteFolder = useDeleteFolder();
  const updateFolder = useUpdateFolder();
  const removeWheelFromFolder = useRemoveWheelFromFolder();

  const loggedInUser = getLoggedInUser();
  const currentUserId = useParams()?.id;
  const isLoggedInUserProfile = currentUserId == loggedInUser?.id;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isLoading: isInitialLoading,
  } = useGetUserFolders({
    userId: currentUserId,
    parentId: currentFolderId,
    search: debouncedSearchText,
  });

  // Debounce search text updates
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchedText);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchedText]);

  const handleWheelDelete = useCallback(
    (wheelId) => {
      try {
        queryClient.setQueriesData(["folders"], (oldData) => {
          if (!oldData) return oldData;

          // Create a new data object with the wheel removed from all pages
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              results: page.results.filter(
                (item) => !(item.type === 2 && item.id === wheelId)
              ),
            })),
          };
        });
      } catch (error) {
        console.error("Failed to update cache after wheel deletion:", error);
        // If cache update fails, fall back to refetch
        queryClient.invalidateQueries(["folders"]);
      }
    },
    [queryClient]
  );

  const handleWheelReactivate = useCallback(
    (wheelId) => {
      try {
        // Update all queries that match this key
        queryClient.setQueriesData(["folders"], (oldData) => {
          if (!oldData) return oldData;

          console.log({ oldData });
          // Create a new data object with the wheel updated in all pages
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              results: page.results.map((item) =>
                item.type === 2 && item.id === wheelId
                  ? { ...item, is_active: true }
                  : item
              ),
            })),
          };
        });
      } catch (error) {
        console.error(
          "Failed to update cache after wheel reactivation:",
          error
        );
        // If cache update fails, fall back to refetch
        queryClient.invalidateQueries(["folders"]);
      }
    },
    [queryClient]
  );

  const createFolderMutation = useCreateFolder();

  const handleScroll = useCallback(
    (e) => {
      const scrollElement = document.getElementById("MainScrollableView");
      if (!scrollElement) return;

      const { scrollTop, clientHeight, scrollHeight } = scrollElement;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      if (scrollPercentage > 0.7 && !isFetchingNextPage && hasNextPage) {
        console.log("Fetching next page...");
        fetchNextPage();
      }
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  useEffect(() => {
    const scrollElement = document.getElementById("MainScrollableView");
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);
      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const handleFolderDoubleClick = (folder) => {
    setCurrentFolderId(folder.id);
    setFolderPath([...folderPath, folder]);
    setSearchedText(""); // Clear search when navigating
  };

  const handleBackClick = () => {
    const newPath = [...folderPath];
    newPath.pop();
    setFolderPath(newPath);
    setCurrentFolderId(
      newPath.length > 0 ? newPath[newPath.length - 1].id : null
    );
  };

  const getCurrentFolderName = () => {
    if (folderPath.length === 0) return "My Folders";
    return folderPath[folderPath.length - 1].name;
  };

  const handleCreateOrUpdate = async (folderTitle, status, exclude) => {
    try {
      const visibility = status.everyone ? 1 : 2;
      const payload = {
        name: folderTitle,
        visibility,
        gallery_visibility: exclude,
      };

      if (isUpdateMode && selectedFolder) {
        // Update existing folder
        const response = await updateFolder.mutateAsync({
          id: selectedFolder.id,
          ...payload,
        });
        if (response?.data?.success) {
          // Update the folder name in the breadcrumb path
          setFolderPath((prevPath) =>
            prevPath.map((folder) =>
              folder.id === selectedFolder.id
                ? { ...folder, name: folderTitle }
                : folder
            )
          );
          toast.success("Folder updated successfully");
          refetch();
          handleModalClose();
        }
      } else {
        // Create new folder
        payload.parent = currentFolderId;
        payload.tags = [{ name: "Important" }];
        const response = await createFolderMutation.mutateAsync(payload);
        if (response?.data?.success) {
          toast.success(response.data.message);
          refetch();
          handleModalClose();
        }
      }
    } catch (error) {
      console.error("Error handling folder:", error);
      toast.error(
        isUpdateMode ? "Failed to update folder" : "Failed to create folder"
      );
    }
  };

  const handleModalClose = () => {
    setIsCreateModalVisible(false);
    setTimeout(() => {
      setIsUpdateMode(false);
      setSelectedFolder(null);
    }, 300);
  };

  const handleSettingsClick = (folder) => {
    setSelectedFolder(folder);
    setIsUpdateMode(true);
    setIsCreateModalVisible(true);
  };

  // Modified to handle pre-sorted API response
  const { folders: foldersList, wheels: wheelsList } = useMemo(() => {
    if (!data?.pages) return { folders: [], wheels: [] };

    const allItems = data.pages.reduce((acc, page) => {
      if (!page?.results) return acc;
      return [...acc, ...page.results];
    }, []);

    // Find the first wheel (type === 2) to determine the split point
    const wheelStartIndex = allItems.findIndex((item) => item.type === 2);

    if (wheelStartIndex === -1) {
      // No wheels found, all items are folders
      return {
        folders: allItems,
        wheels: [],
      };
    }

    // Split the array at the wheel start index
    return {
      folders: allItems.slice(0, wheelStartIndex),
      wheels: allItems.slice(wheelStartIndex),
    };
  }, [data]);

  const hasAnyFolders = foldersList.length > 0;

  // Debug logs
  useEffect(() => {
    if (data?.pages) {
      console.log("Current folders:", foldersList);
    }
  }, [data, foldersList]);

  const handleBreadcrumbClick = (folder) => {
    if (!folder) {
      // Clicking "My Folders" - reset to root
      setCurrentFolderId(null);
      setFolderPath([]);
      return;
    }

    // Find the index of the clicked folder in the path
    const index = folderPath.findIndex((f) => f.id === folder.id);
    if (index !== -1) {
      // Slice the path up to the clicked folder
      const newPath = folderPath.slice(0, index + 1);
      setFolderPath(newPath);
      setCurrentFolderId(folder.id);
    }
  };

  const handleDeleteFolder = (folder) => {
    dispatch(
      openModal({
        title: "Delete Folder",
        subtitle: "Are you sure you want to delete this Folder?",
        onCancel: () => dispatch(closeModal()),
        onConfirm: () => {
          setIsLoading(true);
          deleteFolder.mutate(folder?.id, {
            onSuccess: () => {
              refetch();
              toast.success("Folder Deleted Successfully");
              dispatch(closeModal());
            },
            onError: () => {
              toast.error("Failed to delete folder");
            },
            onSettled: () => {
              setIsLoading(false);
            },
          });
        },
      })
    );
  };

  const handleVisibilityChange = (folder) => {
    console.log({ folder });
    const newVisibility = folder.visibility === 1 ? 2 : 1;
    setIsLoading(true);
    updateFolder.mutate(
      {
        id: folder.id,
        visibility: newVisibility,
      },
      {
        onSuccess: () => {
          toast.success(
            `Folder is now ${newVisibility === 1 ? "public" : "private"}`
          );
          refetch();
        },
        onError: () => {
          toast.error("Failed to update folder visibility");
        },
        onSettled: () => {
          setIsLoading(false);
        },
      }
    );
  };

  const handleMoveFolder = (folder) => {
    setMoveModalData({
      isOpen: true,
      sourceId: folder.id,
      sourceType: "folder",
    });
  };

  const handleMoveComplete = async ({
    sourceType,
    sourceId,
    targetFolderId,
  }) => {
    try {
      setIsLoading(true);
      // Note: No need to call updateFolder.mutateAsync here since the move is already handled by useMoveFolder
      toast.success(
        `${sourceType === "wheel" ? "Wheel" : "Folder"} moved successfully`
      );
      // Refetch the current folder's contents to update the UI
      await refetch();
    } catch (error) {
      console.error(`Error moving ${sourceType}:`, error);
      toast.error(`Failed to move ${sourceType}`);
    } finally {
      setIsLoading(false);
      setMoveModalData({ isOpen: false, sourceId: null, sourceType: null });
    }
  };

  const handleRemoveWheelFromFolder = (wheelId) => {
    if (!currentFolderId) return;

    dispatch(
      openModal({
        title: "Remove Wheel",
        subtitle: "Are you sure you want to remove this wheel from the folder?",
        onCancel: () => dispatch(closeModal()),
        onConfirm: () => {
          setIsLoading(true);
          removeWheelFromFolder.mutate(
            { folder_id: currentFolderId, wheel_id: wheelId },
            {
              onSuccess: () => {
                toast.success("Wheel removed from folder successfully");
                refetch();
                dispatch(closeModal());
              },
              onError: () => {
                toast.error("Failed to remove wheel from folder");
              },
              onSettled: () => {
                setIsLoading(false);
              },
            }
          );
        },
      })
    );
  };

  // Pass the delete handler to FolderTile
  const renderFolders = () => {
    const filteredFolders = foldersList.filter((folder) => {
      if (isLoggedInUserProfile) {
        // If viewing their own profile, show all folders (public or private) for management
        return true;
      } else {
        // If viewing someone else's profile OR is a guest
        // Only show public folders if the user is logged in (i.e., not a guest)
        return folder.visibility === 1;
      }
    });

    return isGridSelect ? (
      <Grid container spacing={2} sx={{ pt: 2 }}>
        {filteredFolders.map((folder) => (
          <Grid item xs={12} sm={6} md={3} key={folder.id}>
            <FolderTile
              id={folder.id}
              name={folder.name}
              itemCount={0}
              lastModified={folder.updated_at}
              visibility={folder.visibility}
              galleryVisibility={folder.gallery_visibility}
              onDoubleClick={() => handleFolderDoubleClick(folder)}
              onDeleteClick={() => handleDeleteFolder(folder)}
              onVisibilityChange={() => handleVisibilityChange(folder)}
              onSettingsClick={() => handleSettingsClick(folder)}
              onMoveClick={() => handleMoveFolder(folder)}
              hideThreeDotIcon={!isLoggedInUserProfile} //enable only for logged in user
            />
          </Grid>
        ))}
      </Grid>
    ) : (
      <ListView
        items={filteredFolders}
        onItemDoubleClick={handleFolderDoubleClick}
        onItemSettingsClick={handleSettingsClick}
        onItemVisibilityChange={handleVisibilityChange}
        onItemDeleteClick={handleDeleteFolder}
        onItemMoveClick={handleMoveFolder}
        isUserProfile={isLoggedInUserProfile}
      />
    );
  };

  const emptyStateText = searchedText
    ? "No folders match your search"
    : isLoggedInUserProfile
    ? "Your folder is empty!"
    : "The Folder is empty !";
  const emptyStateDescription = searchedText
    ? "Looks like there are no folders matching your search. Try a different search term. "
    : isLoggedInUserProfile
    ? "Looks like there's nothing here yet. Start organizing by adding wheels or creating new folders."
    : "Looks like there's nothing here yet. ";
  return (
    <div>
      <ListHeader
        tabChild={tabChild}
        searchedText={searchedText}
        isGridSelect={isGridSelect}
        onSearchChange={(text) => setSearchedText(text)}
        onSearchSubmit={() => console.log("Search submitted:", searchedText)}
        onViewToggle={setIsGridSelect}
        isUserProfile={isLoggedInUserProfile}
      />
      <FoldersHeader
        title={getCurrentFolderName()}
        showArrowIcon={folderPath.length > 0}
        showCreateFolderIcon={isLoggedInUserProfile}
        onCreateFolderIconPress={() => setIsCreateModalVisible(true)}
        onArrowClick={handleBackClick}
        folderPath={folderPath}
        onBreadcrumbClick={handleBreadcrumbClick}
        onSettingsClick={handleSettingsClick}
      />

      {/* Folders Section */}
      {foldersList.length === 0 &&
      wheelsList.length === 0 &&
      !isInitialLoading ? (
        <EmptyState
          title={emptyStateText}
          showButton={isLoggedInUserProfile}
          descriptionText={emptyStateDescription}
          buttonText="Create a folder"
          onButtonClick={() => setIsCreateModalVisible(true)}
          height={30}
          NestedFolder={true}
          backgroundColor="#f5f5f5"
        />
      ) : (
        renderFolders()
      )}

      {/* Wheels Section */}
      {wheelsList.length > 0 && (
        <>
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 600,
                color: "#123051",
              }}
            >
              Wheels
            </Typography>
          </Box>
          {isGridSelect ? (
            <Grid container spacing={2}>
              {wheelsList.map((wheel) => (
                <Grid item xs={12} sm={6} md={3} key={wheel.id}>
                  <WheelCard
                    isInsideFolder={true}
                    data={wheel}
                    onWheelDelete={handleWheelDelete}
                    onReactivate={handleWheelReactivate}
                    customMenuItems={
                      isLoggedInUserProfile
                        ? [
                            getRemoveFromFolderMenuItem(() =>
                              handleRemoveWheelFromFolder(wheel.id)
                            ),
                          ]
                        : []
                    }
                    excludeMenuItems={[menuItemKeys.MOVE]}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <WheelListView
              wheels={wheelsList}
              onWheelDelete={handleWheelDelete}
              onWheelReactivate={handleWheelReactivate}
              customMenuItems={
                isLoggedInUserProfile
                  ? [getRemoveFromFolderMenuItem(handleRemoveWheelFromFolder)]
                  : []
              }
              isInsideFolder={true}
              excludeMenuItems={[menuItemKeys.MOVE]}
            />
          )}
        </>
      )}

      {(isFetchingNextPage || isInitialLoading) && <FolderSkeleton />}

      {!isFetchingNextPage && !hasNextPage && hasAnyFolders && (
        <Box
          sx={{
            textAlign: "center",
            py: 3,
            // background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(240,240,240,1) 100%)',
            borderRadius: "8px",
            my: 2,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: colors.lightGrey,
              fontWeight: "bold",
              textTransform: "uppercase",
              letterSpacing: "1px",
              fontSize: "0.875rem",
            }}
          >
            No More Items to View
          </Typography>
        </Box>
      )}

      <CreateFolderModal
        isVisible={isCreateModalVisible}
        onClose={handleModalClose}
        onCreateButtonPress={handleCreateOrUpdate}
        onCancelButtonPress={handleModalClose}
        isLoading={isLoading}
        editMode={isUpdateMode}
        folderData={selectedFolder}
      />

      <MoveToFolderModal
        isOpen={moveModalData.isOpen}
        sourceType={moveModalData.sourceType}
        sourceId={moveModalData.sourceId}
        onMovePress={handleMoveComplete}
        onClose={() =>
          setMoveModalData({ isOpen: false, sourceId: null, sourceType: null })
        }
        onCancelPress={() =>
          setMoveModalData({ isOpen: false, sourceId: null, sourceType: null })
        }
      />
    </div>
  );
};

export default Folders;
